/**
 * submittedRequest — reads the launch payload the AM actually submitted.
 *
 * ONE rule governs this module: displayed values come from `status.inputs` and
 * from nowhere else. Not from the email, not from `metadata.client_context`,
 * not from a prefill cache, not from a schema default. The three defects
 * research traced to the silently-discarded-field hole are all of that shape,
 * and a surface that fills a gap from a second source is a surface that lies
 * about what was launched.
 *
 * The ONE exception is the reuse IDENTITY, and it is an exception because it is
 * not a displayed value: `status.metadata.email.internet_message_id` is written
 * by the add-in launch path, is not in `inputs`, and is what a later
 * RE-ANALYSE (owned by `prefill-drawer`) re-derives from. It is never taken
 * from whatever mail item happens to be selected — reading the selected item
 * here is how a reuse silently re-analyses the wrong email.
 */

import type { InstanceStatusSnapshot } from "./types";

/**
 * The nine declared per-action fields, in DISPLAY order. Renaming one here is a
 * contract change, not a cosmetic edit — it is the whitelist, and no undeclared
 * key is displayed even when `inputs` carries it.
 */
export const DECLARED_ACTION_FIELDS = [
  "policy_number",
  "policy_type_code",
  "term_effective_date",
  "term_expiration_date",
  "change_type",
  "interest_type",
  "target_interest",
  "mortgagees",
  "effective_date",
] as const;

export type DeclaredActionField = (typeof DECLARED_ACTION_FIELDS)[number];

export const DECLARED_FIELD_LABELS: Record<DeclaredActionField, string> = {
  policy_number: "Policy number",
  policy_type_code: "Policy type",
  term_effective_date: "Term effective",
  term_expiration_date: "Term expiration",
  change_type: "Change type",
  interest_type: "Interest type",
  target_interest: "Target interest",
  mortgagees: "Interest records",
  effective_date: "Effective date",
};

/**
 * Pre-reshape launches carry the same VALUES under three older names. This is
 * a DECLARED rename map from `action-list-contract`, not an inference.
 */
const LEGACY_KEY_ALIASES: Partial<Record<DeclaredActionField, string>> = {
  policy_type_code: "ams_policy_type_code",
  term_effective_date: "policy_effective_date",
  term_expiration_date: "policy_expiration_date",
};

/** Visible, distinct, and never an em-dash: an em-dash reads as "empty OR not
 *  loaded", and those are different facts (PRD "honest states"). */
export const NOT_SUPPLIED = "Not supplied";

export interface SubmittedField {
  key: DeclaredActionField;
  label: string;
  value: unknown | null;
  supplied: boolean;
}

export interface SubmittedRequestModel {
  shape: "per_action" | "legacy_flat";
  fields: SubmittedField[];
  /** null = this payload does not carry the run-level choice. NOT `true`, and
   *  NOT "off" — those are two different claims and neither is supported. */
  draftReply: boolean | null;
  /** `status.metadata.email.internet_message_id`, for RE-ANALYSE on reuse. */
  identity: string | null;
  /** EXACTLY the displayed set. The probe asserts the launch surface receives
   *  what the AM read, so any divergence is a lie in both directions. */
  reuseValues: Record<string, unknown>;
}

function record(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function readIdentity(status: InstanceStatusSnapshot | null): string | null {
  const metadata = record(
    (status as { metadata?: unknown } | null)?.metadata ?? null,
  );
  const email = record(metadata?.["email"] ?? null);
  const id = email?.["internet_message_id"];
  return typeof id === "string" && id !== "" ? id : null;
}

export function readSubmittedRequest(
  status: InstanceStatusSnapshot | null,
): SubmittedRequestModel | null {
  const inputs = record((status as { inputs?: unknown } | null)?.inputs ?? null);
  if (!inputs) return null;

  const actions = inputs["actions"];
  const firstAction = Array.isArray(actions) ? record(actions[0]) : null;

  // `legacy_flat` ONLY when the reshaped array is absent AND at least one
  // legacy key is present. Absent-and-absent is a per-action payload with
  // nothing in it, which is a different fact.
  const hasLegacyKey = Object.values(LEGACY_KEY_ALIASES).some(
    (alias) => alias != null && alias in inputs,
  );
  const shape: SubmittedRequestModel["shape"] =
    firstAction == null && hasLegacyKey ? "legacy_flat" : "per_action";

  const source: Record<string, unknown> =
    shape === "per_action" ? (firstAction ?? inputs) : inputs;

  const fields: SubmittedField[] = DECLARED_ACTION_FIELDS.map((key) => {
    let value: unknown = source[key];
    if (value === undefined && shape === "legacy_flat") {
      const alias = LEGACY_KEY_ALIASES[key];
      if (alias) value = inputs[alias];
    }
    // Term identity was RUN level before the reshape; on a per-action payload
    // that still carries it at run level, read it there rather than call a
    // supplied value "not supplied".
    if (value === undefined && shape === "per_action" && key in inputs) {
      value = inputs[key];
    }
    const supplied = value !== undefined && value !== null;
    return {
      key,
      label: DECLARED_FIELD_LABELS[key],
      value: supplied ? value : null,
      supplied,
    };
  });

  const rawDraftReply = inputs["draft_reply"];
  const draftReply =
    typeof rawDraftReply === "boolean" ? rawDraftReply : null;

  const reuseValues: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.supplied) reuseValues[f.key] = f.value;
  }
  if (draftReply !== null) reuseValues["draft_reply"] = draftReply;

  return {
    shape,
    fields,
    draftReply,
    identity: readIdentity(status),
    reuseValues,
  };
}
